import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  deleteDoc,        // for deleting docs
  getDocs,          // NEW: to iterate subcollections
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import styles from "../styles/ClientWeeklyWorkHubPage.module.css";

const EDIT_ROLES = ["Accounts Manager", "Manager"];

/* ---------- Week helpers (Mon–Sun) ---------- */
const startOfWeekMonday = (dLike) => {
  const d = new Date(dLike);
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  const diff = (dow + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const rangeLabel = (monday) => {
  const end = addDays(monday, 6);
  const f = (dt) => dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const sameYear = monday.getFullYear() === end.getFullYear();
  return `${f(monday)} – ${f(end)}${sameYear ? "" : " " + end.getFullYear()}`;
};

const emptyForm = {
  highlights: "",
  blockers: "",
  nextWeek: "",
  workItems: [{ text: "", owner: "", status: "Done" }],
  links: [""],
  metrics: [{ key: "", value: "" }],
};
const sanitizeList = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((x) => (typeof x === "string" ? x.trim() : x))
    .filter((x) => (typeof x === "string" ? x.length > 0 : !!x));

/* ---------- Goals helpers ---------- */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const pct = (num) => clamp(Math.round(num), 0, 100);
const deriveProgressForCount = (currentCount, targetCount) => {
  const t = Number(targetCount || 0);
  const c = Number(currentCount || 0);
  if (t <= 0) return 0;
  return pct((c / t) * 100);
};

const emptyGoal = {
  title: "",
  description: "",
  startDate: toYMD(new Date()),
  targetDate: toYMD(addDays(new Date(), 30)),
  status: "Planned", // Planned | In Progress | At Risk | Done
  type: "percent",   // "percent" | "count"
  progress: 0,       // used for "percent"; derived for "count"
  unitLabel: "",     // e.g., "videos", "posts"
  targetCount: 0,
  currentCount: 0,
  relatedWeeks: [],
};

const statusColor = (s) =>
  s === "Done" ? "#047857" :
  s === "At Risk" ? "#9a3412" :
  s === "In Progress" ? "#1d4ed8" :
  "#4b5563";

/** Inline styles for timeline so you can paste this file as-is */
const TL = {
  tabsRow: { display: "flex", gap: 8, alignItems: "center" },
  tab: (active) => ({
    border: "1px solid #e5e7eb",
    background: active ? "#0f172a" : "#fff",
    color: active ? "#fff" : "#0f172a",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  }),
  timelineWrap: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    padding: 12,
  },
  tlRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 10,
    padding: "12px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#f9fafb",
  },
  tlRowInner: { display: "grid", gap: 6 },
  title: { fontWeight: 700, fontSize: 16 },
  desc: { color: "#4b5563", fontSize: 14 },
  meta: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  chip: (bg, color, border) => ({
    background: bg,
    color,
    border: `1px solid ${border}`,
    borderRadius: 999,
    fontSize: 12,
    padding: "3px 8px",
    display: "inline-flex",
    gap: 6,
    alignItems: "center",
  }),
  barWrap: {
    width: "100%",
    height: 10,
    borderRadius: 999,
    background: "#eef2ff",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  bar: (w, color) => ({
    width: `${w}%`,
    height: "100%",
    background: color,
    transition: "width 220ms ease",
  }),
  group: { display: "grid", gap: 10 },
  groupTitle: { fontWeight: 800, color: "#111827", marginTop: 8 },
  goalsToolbar: { display: "flex", gap: 8, alignItems: "center" },
  search: {
    padding: "8px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    outline: "none",
    minWidth: 220,
  },
  select: {
    padding: "8px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    outline: "none",
    background: "#fff",
  },
  spacer: { flex: 1 },
  ghostBtn: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#111827",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
  },
  primaryBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "1px solid #2563eb",
    borderRadius: 10,
    padding: "10px 14px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

/* ---------- Small icon + icon buttons (no deps) ---------- */
const Icon = ({ name, size = 16 }) => {
  const common = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor", strokeWidth: 2,
    strokeLinecap: "round", strokeLinejoin: "round"
  };
  if (name === "edit") {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
      </svg>
    );
  }
  if (name === "trash") {
    return (
      <svg {...common} aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      </svg>
    );
  }
  return null;
};

const ICONBTN = {
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    lineHeight: 1,
  },
  danger: {
    border: "1px solid #dc2626",
    color: "#dc2626",
    background: "#fff",
  },
};

export default function ClientWeeklyWorkHubPage() {
  const { userRole, currentUser } = useAuth();
  const canEdit = EDIT_ROLES.includes(userRole);
  const isManager = userRole === "Manager"; // NEW: manager-only controls
  const isMounted = useRef(true);

  /* ---------- Left list: clients that already have weekly work ---------- */
  const [clientsWithWork, setClientsWithWork] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [loadingClients, setLoadingClients] = useState(true);
  const [deletingClientId, setDeletingClientId] = useState(null); // NEW

  /* ---------- All clients (for the “new” modal picker) ---------- */
  const [allClients, setAllClients] = useState([]);
  const [loadingAllClients, setLoadingAllClients] = useState(true);

  /* ---------- Selection & state ---------- */
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientName, setSelectedClientName] = useState("");

  const [weeks, setWeeks] = useState([]); // weeklyReports docs for selected client
  const [activeWeekKey, setActiveWeekKey] = useState("");
  const [loadingWeeks, setLoadingWeeks] = useState(false);
  const [deletingWeekId, setDeletingWeekId] = useState(null); // NEW

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ---------- Modal form state (weekly work) ---------- */
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Form’s target client (so you can open the modal without a selected client)
  const [formClientId, setFormClientId] = useState("");
  const [formClientName, setFormClientName] = useState("");
  const [formWeekKey, setFormWeekKey] = useState(toYMD(startOfWeekMonday(new Date())));

  const activeWeekDoc = useMemo(
    () => weeks.find((w) => w.id === activeWeekKey),
    [weeks, activeWeekKey]
  );

  /* ---------- Goals state ---------- */
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [goalsTab, setGoalsTab] = useState("reports"); // "reports" | "goals"
  const [goalSearch, setGoalSearch] = useState("");
  const [goalStatusFilter, setGoalStatusFilter] = useState("All"); // All, Planned, In Progress, At Risk, Done

  // Goal modal
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalMode, setGoalMode] = useState("create"); // "create" | "edit"
  const [goalEditingId, setGoalEditingId] = useState(null);
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalForm, setGoalForm] = useState(emptyGoal);

  // deleting state
  const [deletingGoalId, setDeletingGoalId] = useState(null);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  /* ---------- Stream: clients that already have weekly work ---------- */
  useEffect(() => {
    const clientsRef = collection(db, "clients");
    const qRef = query(clientsRef, where("hasWeeklyReports", "==", true));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        if (!isMounted.current) return;
        const list = snap.docs
          .map((d) => ({ id: d.id, name: d.data().name || d.id }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setClientsWithWork(list);
        setLoadingClients(false);

        if (selectedClientId) {
          const hit = list.find((c) => c.id === selectedClientId);
          if (hit) setSelectedClientName(hit.name);
        }
      },
      (err) => {
        console.error("Load clientsWithWork error:", err);
        if (isMounted.current) {
          setError("Failed to load clients.");
          setLoadingClients(false);
        }
      }
    );
    return () => unsub();
  }, [selectedClientId]);

  /* ---------- Stream: all clients for picker ---------- */
  useEffect(() => {
    const ref = collection(db, "clients");
    const qRef = query(ref, orderBy("name", "asc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        if (!isMounted.current) return;
        const list = snap.docs.map((d) => ({ id: d.id, name: d.data().name || d.id }));
        setAllClients(list);
        setLoadingAllClients(false);
      },
      (err) => {
        console.error("Load allClients error:", err);
        if (isMounted.current) setLoadingAllClients(false);
      }
    );
    return () => unsub();
  }, []);

  const filteredClients = useMemo(() => {
    const s = clientSearch.trim().toLowerCase();
    if (!s) return clientsWithWork;
    return clientsWithWork.filter((c) => c.name.toLowerCase().includes(s));
  }, [clientsWithWork, clientSearch]);

  /* ---------- Stream: weekly reports for selected client ---------- */
  useEffect(() => {
    setWeeks([]);
    setActiveWeekKey("");
    setLoadingWeeks(false);
    setError("");
    if (!selectedClientId) return;

    setLoadingWeeks(true);
    const colRef = collection(db, "clients", selectedClientId, "weeklyReports");
    const qRef = query(colRef, orderBy("weekStart", "desc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        if (!isMounted.current) return;
        const items = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id, // weekKey (yyyy-mm-dd)
            ...data,
            weekStart: data.weekStart?.toDate?.() || new Date(d.id),
            weekEnd: data.weekEnd?.toDate?.() || addDays(new Date(d.id), 6),
          };
        });
        setWeeks(items);
        if (items.length && !activeWeekKey) setActiveWeekKey(items[0].id);
        setLoadingWeeks(false);
      },
      (err) => {
        console.error("Load weeklyReports error:", err);
        if (isMounted.current) {
          setError("Failed to load weekly reports.");
          setLoadingWeeks(false);
        }
      }
    );
    return () => unsub();
  }, [selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Stream goals for selected client ---------- */
  useEffect(() => {
    setGoals([]);
    setLoadingGoals(false);
    if (!selectedClientId) return;

    setLoadingGoals(true);
    const colRef = collection(db, "clients", selectedClientId, "goals");
    const qRef = query(colRef, orderBy("targetDate", "asc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        if (!isMounted.current) return;
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGoals(items);
        setLoadingGoals(false);
      },
      (err) => {
        console.error("Load goals error:", err);
        if (isMounted.current) setLoadingGoals(false);
      }
    );
    return () => unsub();
  }, [selectedClientId]);

  /* ---------- Open weekly modals ---------- */
  const openCreateFormForClient = (clientId, clientName) => {
    if (!canEdit) return;
    setFormMode("create");
    setForm(emptyForm);
    setFormWeekKey(toYMD(startOfWeekMonday(new Date())));
    setFormClientId(clientId);
    setFormClientName(clientName);
    setShowForm(true);
    setError("");
    setSuccess("");

    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
  };

  const openCreateFormGlobal = () => {
    if (!canEdit) return;
    setFormMode("create");
    setForm(emptyForm);
    setFormWeekKey(toYMD(startOfWeekMonday(new Date())));
    setFormClientId("");
    setFormClientName("");
    setShowForm(true);
    setError("");
    setSuccess("");
  };

  const openEditFormForWeek = (weekId) => {
    if (!canEdit) return;
    const hit = weeks.find((w) => w.id === weekId);
    if (!hit) return;

    setFormMode("edit");
    setFormWeekKey(hit.id);
    setFormClientId(selectedClientId);
    setFormClientName(selectedClientName);
    setForm({
      highlights: hit.highlights || "",
      blockers: hit.blockers || "",
      nextWeek: hit.nextWeek || "",
      workItems:
        Array.isArray(hit.workItems) && hit.workItems.length
          ? hit.workItems
          : [{ text: "", owner: "", status: "Done" }],
      links: Array.isArray(hit.links) && hit.links.length ? hit.links : [""],
      metrics:
        Array.isArray(hit.metrics) && hit.metrics.length
          ? hit.metrics
          : [{ key: "", value: "" }],
    });
    setShowForm(true);
  };

  /* ---------- Save weekly report ---------- */
  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!canEdit) return;

    const targetClientId = formClientId;
    const targetClientName =
      formClientName || allClients.find((c) => c.id === targetClientId)?.name || "";

    if (formMode === "create" && !targetClientId) {
      setError("Please select a client.");
      return;
    }

    const links = sanitizeList(form.links);
    const metrics = (Array.isArray(form.metrics) ? form.metrics : [])
      .map((m) => ({ key: (m.key || "").trim(), value: (m.value || "").trim() }))
      .filter((m) => m.key || m.value);

    const workItems = (Array.isArray(form.workItems) ? form.workItems : [])
      .map((w) => ({
        text: (w.text || "").trim(),
        owner: (w.owner || "").trim(),
        status: w.status || "Done",
      }))
      .filter((w) => w.text);

    const monday = startOfWeekMonday(new Date(formWeekKey));
    const payload = {
      clientId: targetClientId,
      clientName: targetClientName,
      weekKey: toYMD(monday),
      weekStart: monday,
      weekEnd: addDays(monday, 6),
      highlights: (form.highlights || "").trim(),
      blockers: (form.blockers || "").trim(),
      nextWeek: (form.nextWeek || "").trim(),
      links,
      metrics,
      workItems,
      updatedAt: serverTimestamp(),
      ...(formMode === "create"
        ? {
            createdAt: serverTimestamp(),
            createdBy: currentUser?.uid || null,
            createdByName:
              currentUser?.displayName || currentUser?.email || "Unknown",
          }
        : {}),
    };

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const weekRef = doc(db, "clients", targetClientId, "weeklyReports", toYMD(monday));
      await setDoc(weekRef, payload, { merge: true });

      const clientRef = doc(db, "clients", targetClientId);
      await setDoc(clientRef, { hasWeeklyReports: true, name: targetClientName }, { merge: true });

      setSelectedClientId(targetClientId);
      setSelectedClientName(targetClientName);

      setSuccess("Saved.");
      setShowForm(false);
    } catch (err) {
      console.error("Save weekly report error:", err);
      setError("Failed to save weekly report.");
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(""), 1500);
    }
  };

  /* ---------- Weekly Form helpers ---------- */
  const setArrayField = (field, updater) =>
    setForm((f) => ({ ...f, [field]: updater(Array.isArray(f[field]) ? f[field] : []) }));

  const updateWorkItem = (idx, key, val) =>
    setArrayField("workItems", (arr) =>
      arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it))
    );
  const addWorkItem = () =>
    setArrayField("workItems", (arr) => [...arr, { text: "", owner: "", status: "Done" }]);
  const removeWorkItem = (idx) =>
    setArrayField("workItems", (arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const updateLink = (idx, val) =>
    setArrayField("links", (arr) => arr.map((v, i) => (i === idx ? val : v)));
  const addLink = () => setArrayField("links", (arr) => [...arr, ""]);
  const removeLink = (idx) =>
    setArrayField("links", (arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const updateMetric = (idx, key, val) =>
    setArrayField("metrics", (arr) =>
      arr.map((m, i) => (i === idx ? { ...m, [key]: val } : m))
    );
  const addMetric = () => setArrayField("metrics", (arr) => [...arr, { key: "", value: "" }]);
  const removeMetric = (idx) =>
    setArrayField("metrics", (arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  /* ---------- Goals — modal open/edit ---------- */
  const openCreateGoal = () => {
    if (!canEdit || !selectedClientId) return;
    setGoalMode("create");
    setGoalEditingId(null);
    setGoalForm({
      ...emptyGoal,
      startDate: toYMD(new Date()),
      targetDate: toYMD(addDays(new Date(), 30)),
      type: "percent",
      progress: 0,
      unitLabel: "",
      targetCount: 0,
      currentCount: 0,
    });
    setShowGoalModal(true);
  };
  const openEditGoal = (g) => {
    if (!canEdit) return;
    setGoalMode("edit");
    setGoalEditingId(g.id);
    const type = g.type === "count" ? "count" : "percent";
    const targetCount = Number(g.targetCount || 0);
    const currentCount = Number(g.currentCount || 0);
    const progress =
      type === "count" ? deriveProgressForCount(currentCount, targetCount)
                        : clamp(Number(g.progress ?? 0), 0, 100);
    setGoalForm({
      title: g.title || "",
      description: g.description || "",
      startDate: g.startDate || toYMD(new Date()),
      targetDate: g.targetDate || toYMD(addDays(new Date(), 30)),
      status: g.status || "Planned",
      type,
      progress,
      unitLabel: g.unitLabel || "",
      targetCount,
      currentCount,
      relatedWeeks: Array.isArray(g.relatedWeeks) ? g.relatedWeeks : [],
    });
    setShowGoalModal(true);
  };

  /* ---------- Save Goal ---------- */
  const saveGoal = async (e) => {
    e?.preventDefault?.();
    if (!canEdit || !selectedClientId) return;

    const type = goalForm.type === "count" ? "count" : "percent";
    let targetCount = Number(goalForm.targetCount || 0);
    let currentCount = Number(goalForm.currentCount || 0);
    let progress = clamp(Number(goalForm.progress || 0), 0, 100);

    if (type === "count") {
      if (!goalForm.unitLabel.trim()) {
        setError("Unit label is required for count goals (e.g., videos, posts).");
        return;
      }
      if (targetCount <= 0) {
        setError("Target count must be greater than 0 for count goals.");
        return;
      }
      currentCount = Math.max(0, currentCount);
      targetCount = Math.max(1, targetCount);
      progress = deriveProgressForCount(currentCount, targetCount);
    }

    const payload = {
      title: (goalForm.title || "").trim(),
      description: (goalForm.description || "").trim(),
      startDate: goalForm.startDate,
      targetDate: goalForm.targetDate,
      status: goalForm.status,
      type,
      progress,
      unitLabel: type === "count" ? goalForm.unitLabel.trim() : "",
      targetCount: type === "count" ? targetCount : 0,
      currentCount: type === "count" ? currentCount : 0,
      relatedWeeks: (Array.isArray(goalForm.relatedWeeks) ? goalForm.relatedWeeks : [])
        .map((w) => String(w).trim())
        .filter(Boolean),
      updatedAt: serverTimestamp(),
      ...(goalMode === "create"
        ? {
            createdAt: serverTimestamp(),
            createdBy: currentUser?.uid || null,
            createdByName:
              currentUser?.displayName || currentUser?.email || "Unknown",
          }
        : {}),
    };

    if (!payload.title) { setError("Goal title is required."); return; }
    if (!payload.startDate || !payload.targetDate) {
      setError("Start & target dates are required.");
      return;
    }

    setGoalSaving(true);
    setError("");
    setSuccess("");

    try {
      const colRef = collection(db, "clients", selectedClientId, "goals");
      const id = goalEditingId || payload.title.toLowerCase().replace(/\s+/g, "-");
      const ref = doc(colRef, id);
      await setDoc(ref, payload, { merge: true });

      const clientRef = doc(db, "clients", selectedClientId);
      await setDoc(clientRef, { name: selectedClientName, hasWeeklyReports: true }, { merge: true });

      setSuccess("Goal saved.");
      setShowGoalModal(false);
    } catch (err) {
      console.error("Save goal error:", err);
      setError("Failed to save goal.");
    } finally {
      setGoalSaving(false);
      setTimeout(() => setSuccess(""), 1500);
    }
  };

  /* ---------- Delete Goal ---------- */
  const deleteGoal = async (goalId) => {
    if (!canEdit || !selectedClientId || !goalId) return;
    const ok = window.confirm("Delete this goal permanently?");
    if (!ok) return;

    setDeletingGoalId(goalId);
    setError("");
    setSuccess("");

    try {
      const ref = doc(db, "clients", selectedClientId, "goals", goalId);
      await deleteDoc(ref);
      setSuccess("Goal deleted.");
      if (showGoalModal && goalEditingId === goalId) setShowGoalModal(false);
    } catch (err) {
      console.error("Delete goal error:", err);
      setError("Failed to delete goal.");
    } finally {
      setDeletingGoalId(null);
      setTimeout(() => setSuccess(""), 1500);
    }
  };

  /* ---------- Quick goal updates inside Weekly Report ---------- */
  const quickUpdateGoal = async (goalId, patch) => {
    if (!selectedClientId) return;
    try {
      const ref = doc(db, "clients", selectedClientId, "goals", goalId);
      await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
      setSuccess("Goal updated.");
      setTimeout(() => setSuccess(""), 1200);
    } catch (err) {
      console.error("quickUpdateGoal error:", err);
      setError("Failed to update goal.");
    }
  };

  // Toggle this week in goal.relatedWeeks
  const toggleThisWeekOnGoal = async (goal, shouldLink, weekKey) => {
    if (!selectedClientId) return;
    const current = Array.isArray(goal.relatedWeeks) ? goal.relatedWeeks : [];
    const set = new Set(current);
    if (shouldLink) set.add(weekKey);
    else set.delete(weekKey);
    await quickUpdateGoal(goal.id, { relatedWeeks: Array.from(set) });
  };

  /* ---------- NEW: Delete a week ---------- */
  const deleteWeek = async (clientId, weekId) => {
    if (!isManager || !clientId || !weekId) return;
    const ok = window.confirm(`Delete week "${weekId}" for this client? This cannot be undone.`);
    if (!ok) return;

    setDeletingWeekId(weekId);
    setError("");
    setSuccess("");

    try {
      // Delete the week doc
      await deleteDoc(doc(db, "clients", clientId, "weeklyReports", weekId));

      // Check if there are any weeks left; if none, unset hasWeeklyReports
      const remaining = await getDocs(collection(db, "clients", clientId, "weeklyReports"));
      if (remaining.empty) {
        await setDoc(doc(db, "clients", clientId), { hasWeeklyReports: false }, { merge: true });
      }

      // If the deleted week was active, clear selection or switch to most recent
      if (activeWeekKey === weekId) {
        setActiveWeekKey("");
      }

      setSuccess("Week deleted.");
    } catch (err) {
      console.error("Delete week error:", err);
      setError("Failed to delete week.");
    } finally {
      setDeletingWeekId(null);
      setTimeout(() => setSuccess(""), 1500);
    }
  };

  /* ---------- NEW: Delete a client and all sub-data ---------- */
  const deleteClientWithData = async (clientId, clientName) => {
    if (!isManager || !clientId) return;
    const ok = window.confirm(
      `Delete client "${clientName || clientId}" and ALL their data (weekly reports & goals)?`
    );
    if (!ok) return;

    setDeletingClientId(clientId);
    setError("");
    setSuccess("");

    try {
      // Delete all weeklyReports
      const weeksSnap = await getDocs(collection(db, "clients", clientId, "weeklyReports"));
      const weekDeletes = weeksSnap.docs.map((d) =>
        deleteDoc(doc(db, "clients", clientId, "weeklyReports", d.id))
      );
      await Promise.all(weekDeletes);

      // Delete all goals
      const goalsSnap = await getDocs(collection(db, "clients", clientId, "goals"));
      const goalDeletes = goalsSnap.docs.map((d) =>
        deleteDoc(doc(db, "clients", clientId, "goals", d.id))
      );
      await Promise.all(goalDeletes);

      // Finally delete client doc
      await deleteDoc(doc(db, "clients", clientId));

      // Clear selection if this was selected
      if (selectedClientId === clientId) {
        setSelectedClientId("");
        setSelectedClientName("");
        setWeeks([]);
        setActiveWeekKey("");
        setGoals([]);
      }

      setSuccess("Client and all related data deleted.");
    } catch (err) {
      console.error("Delete client error:", err);
      setError("Failed to delete client.");
    } finally {
      setDeletingClientId(null);
      setTimeout(() => setSuccess(""), 1800);
    }
  };

  /* ---------- Derived: filtered goals for timeline ---------- */
  const filteredGoals = useMemo(() => {
    const s = goalSearch.trim().toLowerCase();
    return goals
      .filter((g) => goalStatusFilter === "All" || (g.status || "Planned") === goalStatusFilter)
      .filter((g) =>
        s
          ? (g.title || "").toLowerCase().includes(s) ||
            (g.description || "").toLowerCase().includes(s)
          : true
      );
  }, [goals, goalSearch, goalStatusFilter]);

  // Group goals by year (timeline headers)
  const goalsByYear = useMemo(() => {
    const map = new Map();
    filteredGoals.forEach((g) => {
      const year = (g.targetDate || "").slice(0, 4) || "Other";
      if (!map.has(year)) map.set(year, []);
      map.get(year).push(g);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredGoals]);

  /* ---------- Render ---------- */
  const monday = startOfWeekMonday(new Date(formWeekKey));

  return (
    <div className={styles.page}>
      {/* LEFT: clients with weekly work */}
      <div className={styles.leftPane}>
        <div className={styles.leftHeader}>
          <h2 className={styles.h}>Clients with Weekly Work</h2>
          <input
            type="search"
            placeholder="Search clients…"
            className={styles.search}
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            disabled={loadingClients}
          />
        </div>

        {loadingClients ? (
          <div className={styles.dim}>Loading clients…</div>
        ) : filteredClients.length === 0 ? (
          <div className={styles.empty}>
            No clients have weekly work yet.
          </div>
        ) : (
          <ul className={styles.clientList}>
            {filteredClients.map((c) => (
              <li
                key={c.id}
                className={`${styles.clientItem} ${
                  c.id === selectedClientId ? styles.clientItemActive : ""
                }`}
                onClick={() => {
                  setSelectedClientId(c.id);
                  setSelectedClientName(c.name);
                }}
              >
                <div className={styles.clientName}>{c.name}</div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {canEdit && (
                    <button
                      className={styles.addBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreateFormForClient(c.id, c.name);
                      }}
                      title="Add Client Work"
                    >
                      + Add Client Work
                    </button>
                  )}


                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* RIGHT: details */}
      <div className={styles.rightPane}>
        <div className={styles.rightHeader}>
          <div className={styles.headerTitle}>
            {selectedClientName ? (
              <>
                <h2 className={styles.h}>{selectedClientName}</h2>
                <div className={styles.muted}>
                  {loadingWeeks ? "Loading weekly reports…" : `${weeks.length} week${weeks.length === 1 ? "" : "s"}`}
                </div>
              </>
            ) : (
              <h2 className={styles.h}>Select a client to view details</h2>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            {canEdit && (
              <>
                <button className={styles.primary} onClick={openCreateFormGlobal}>
                  + Add Client Work
                </button>
                {selectedClientId && (
                  <button className={styles.outline} onClick={openCreateGoal}>
                    + Add Goal
                  </button>
                )}
              </>
            )}
            {isManager && selectedClientId && (
              <button
                onClick={() => deleteClientWithData(selectedClientId, selectedClientName)}
                style={{
                  ...ICONBTN.base,
                  ...ICONBTN.danger,
                  padding: "10px 12px",
                  fontWeight: 600,
                }}
                title="Delete Client"
              >
                {deletingClientId === selectedClientId ? "Deleting…" : "Delete Client"}
              </button>
            )}
          </div>
        </div>

        {/* Tabs: Reports | Goals */}
        {selectedClientId && (
          <div style={{ margin: "6px 0 12px" }}>
            <div style={TL.tabsRow}>
              <button
                style={TL.tab(goalsTab === "reports")}
                onClick={() => setGoalsTab("reports")}
              >
                Reports
              </button>
              <button
                style={TL.tab(goalsTab === "goals")}
                onClick={() => setGoalsTab("goals")}
              >
                Goals Timeline
              </button>
            </div>
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        {/* REPORTS VIEW */}
        {selectedClientId && goalsTab === "reports" && (
          <div className={styles.split}>
            {/* Weeks list */}
            <aside className={styles.weekListWrap}>
              <h4 className={styles.subh}>Past Weeks</h4>
              {weeks.length === 0 ? (
                <div className={styles.dim}>No reports yet.</div>
              ) : (
                <ul className={styles.weekList}>
                  {weeks.map((w) => (
                    <li key={w.id} className={styles.weekListItem}>
                      <button
                        className={`${styles.weekBtn} ${
                          w.id === activeWeekKey ? styles.weekBtnActive : ""
                        }`}
                        onClick={() => setActiveWeekKey(w.id)}
                        title={w.highlights || "Open week"}
                      >
                        {w.id} — {rangeLabel(startOfWeekMonday(new Date(w.id)))}
                      </button>

                      {isManager && (
                        <button
                          style={{ ...ICONBTN.base, ...ICONBTN.danger, marginLeft: 8 }}
                          onClick={() => deleteWeek(selectedClientId, w.id)}
                          disabled={deletingWeekId === w.id}
                          title="Delete Week"
                          aria-label="Delete Week"
                        >
                          {deletingWeekId === w.id ? "…" : <Icon name="trash" />}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </aside>

            {/* Week details */}
            <section className={styles.details}>
              {!activeWeekDoc ? (
                <div className={styles.dim}>Pick a week to see details.</div>
              ) : (
                <>
                  <div className={styles.detailsHeader}>
                    <div>
                      <h3 className={styles.subh}>
                        {rangeLabel(startOfWeekMonday(new Date(activeWeekDoc.id)))}{" "}
                        <span className={styles.muted}>({activeWeekDoc.id})</span>
                      </h3>
                      <div className={styles.muted}>
                        Updated:{" "}
                        {activeWeekDoc.updatedAt?.toDate
                          ? activeWeekDoc.updatedAt.toDate().toLocaleString()
                          : "—"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {canEdit && (
                        <button
                          className={styles.outline}
                          onClick={() => openEditFormForWeek(activeWeekDoc.id)}
                        >
                          Edit
                        </button>
                      )}
                      {isManager && (
                        <button
                          style={{ ...ICONBTN.base, ...ICONBTN.danger }}
                          onClick={() => deleteWeek(selectedClientId, activeWeekDoc.id)}
                          disabled={deletingWeekId === activeWeekDoc.id}
                          title="Delete This Week"
                        >
                          {deletingWeekId === activeWeekDoc.id ? "…" : <Icon name="trash" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {activeWeekDoc.highlights && (
                    <div className={styles.block}>
                      <h4 className={styles.blockTitle}>Highlights</h4>
                      <p className={styles.text}>{activeWeekDoc.highlights}</p>
                    </div>
                  )}

                  <div className={styles.block}>
                    <h4 className={styles.blockTitle}>Work Items</h4>
                    {Array.isArray(activeWeekDoc.workItems) && activeWeekDoc.workItems.length ? (
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Owner</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeWeekDoc.workItems.map((it, i) => (
                            <tr key={i}>
                              <td>{it.text}</td>
                              <td>{it.owner}</td>
                              <td>{it.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className={styles.dim}>No items recorded.</div>
                    )}
                  </div>

                  {(activeWeekDoc.links?.length || 0) > 0 && (
                    <div className={styles.block}>
                      <h4 className={styles.blockTitle}>Links</h4>
                      <ul className={styles.linkList}>
                        {activeWeekDoc.links.map((l, i) => (
                          <li key={i}>
                            <a href={l} target="_blank" rel="noopener noreferrer">
                              {l}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(activeWeekDoc.metrics?.length || 0) > 0 && (
                    <div className={styles.block}>
                      <h4 className={styles.blockTitle}>Metrics</h4>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeWeekDoc.metrics.map((m, i) => (
                            <tr key={i}>
                              <td>{m.key}</td>
                              <td>{m.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(activeWeekDoc.blockers || activeWeekDoc.nextWeek) && (
                    <div className={styles.blockGrid2}>
                      {activeWeekDoc.blockers && (
                        <div className={styles.block}>
                          <h4 className={styles.blockTitle}>Blockers / Risks</h4>
                          <p className={styles.text}>{activeWeekDoc.blockers}</p>
                        </div>
                      )}
                      {activeWeekDoc.nextWeek && (
                        <div className={styles.block}>
                          <h4 className={styles.blockTitle}>Next Week</h4>
                          <p className={styles.text}>{activeWeekDoc.nextWeek}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ---- Goals (quick update panel) ---- */}
                  <div className={styles.block}>
                    <h4 className={styles.blockTitle}>Goals</h4>

                    {loadingGoals ? (
                      <div className={styles.dim}>Loading goals…</div>
                    ) : goals.length === 0 ? (
                      <div className={styles.dim}>
                        No goals yet. {canEdit && "Use “+ Add Goal” to create one."}
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const wk = activeWeekDoc.id;
                          const related = goals.filter((g) =>
                            (Array.isArray(g.relatedWeeks) ? g.relatedWeeks : []).includes(wk)
                          );
                          const others = goals.filter(
                            (g) =>
                              !(Array.isArray(g.relatedWeeks) ? g.relatedWeeks : []).includes(wk)
                          );

                          const Row = ({ g }) => {
                            const status = g.status || "Planned";
                            const type = g.type === "count" ? "count" : "percent";
                            const targetCount = Number(g.targetCount || 0);
                            const currentCount = Number(g.currentCount || 0);
                            const computedProgress =
                              type === "count"
                                ? deriveProgressForCount(currentCount, targetCount)
                                : clamp(Number(g.progress ?? 0), 0, 100);
                            const isLinked = (Array.isArray(g.relatedWeeks) ? g.relatedWeeks : []).includes(
                              wk
                            );

                            const updateCounts = (patch) => {
                              const nextCurrent = Number(
                                "currentCount" in patch ? patch.currentCount : g.currentCount || 0
                              );
                              const nextTarget = Number(
                                "targetCount" in patch ? patch.targetCount : g.targetCount || 0
                              );
                              const next = {
                                ...patch,
                                progress:
                                  type === "count"
                                    ? deriveProgressForCount(nextCurrent, nextTarget)
                                    : computedProgress,
                              };
                              quickUpdateGoal(g.id, next);
                            };

                            return (
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    type === "count"
                                      ? "1.5fr 0.9fr 1.1fr 1.1fr 0.9fr auto"
                                      : "1.5fr 0.9fr 0.9fr 0.9fr auto",
                                  gap: 8,
                                  alignItems: "center",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  marginBottom: 8,
                                  background: "#fafafa",
                                }}
                              >
                                {/* Title & dates */}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {g.title}
                                  </div>
                                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    {g.startDate || "?"} → {g.targetDate || "?"}
                                  </div>
                                  {type === "count" && (
                                    <div style={{ fontSize: 12, color: "#1f2937", marginTop: 2 }}>
                                      {currentCount}/{targetCount} {g.unitLabel || ""}
                                    </div>
                                  )}
                                </div>

                                {/* Status */}
                                <div>
                                  <label style={{ fontSize: 12, color: "#6b7280" }}>Status</label>
                                  <select
                                    className={styles.select}
                                    value={status}
                                    onChange={(e) =>
                                      quickUpdateGoal(g.id, { status: e.target.value })
                                    }
                                  >
                                    <option>Planned</option>
                                    <option>In Progress</option>
                                    <option>At Risk</option>
                                    <option>Done</option>
                                  </select>
                                </div>

                                {/* TYPE-SPECIFIC CONTROLS */}
                                {type === "count" ? (
                                  <>
                                    <div>
                                      <label style={{ fontSize: 12, color: "#6b7280" }}>
                                        Current
                                      </label>
                                      <input
                                        type="number"
                                        className={styles.input}
                                        min={0}
                                        value={currentCount}
                                        onChange={(e) =>
                                          updateCounts({ currentCount: Math.max(0, +e.target.value || 0) })
                                        }
                                      />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 12, color: "#6b7280" }}>
                                        Target
                                      </label>
                                      <input
                                        type="number"
                                        className={styles.input}
                                        min={1}
                                        value={targetCount}
                                        onChange={(e) =>
                                          updateCounts({ targetCount: Math.max(1, +e.target.value || 1) })
                                        }
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <div>
                                    <label style={{ fontSize: 12, color: "#6b7280" }}>
                                      Progress (%)
                                    </label>
                                    <input
                                      type="number"
                                      className={styles.input}
                                      min={0}
                                      max={100}
                                      value={computedProgress}
                                      onChange={(e) =>
                                        quickUpdateGoal(g.id, {
                                          progress: clamp(+e.target.value || 0, 0, 100),
                                        })
                                      }
                                    />
                                  </div>
                                )}

                                {/* Link this week */}
                                <div>
                                  <label style={{ fontSize: 12, color: "#6b7280" }}>
                                    Link to this week
                                  </label>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <input
                                      type="checkbox"
                                      checked={isLinked}
                                      onChange={(e) =>
                                        toggleThisWeekOnGoal(g, e.target.checked, wk)
                                      }
                                    />
                                    <span style={{ fontSize: 12, color: "#6b7280" }}>{wk}</span>
                                  </div>
                                </div>

                                {/* Edit / Delete (icon buttons) */}
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                  {canEdit && (
                                    <>
                                      <button
                                        style={ICONBTN.base}
                                        onClick={() => openEditGoal(g)}
                                        type="button"
                                        aria-label="Edit goal"
                                        title="Edit"
                                      >
                                        <Icon name="edit" />
                                      </button>
                                      <button
                                        style={{ ...ICONBTN.base, ...ICONBTN.danger }}
                                        onClick={() => deleteGoal(g.id)}
                                        type="button"
                                        disabled={deletingGoalId === g.id}
                                        aria-label="Delete goal"
                                        title="Delete"
                                      >
                                        {deletingGoalId === g.id ? "…" : <Icon name="trash" />}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          };

                          return (
                            <>
                              {related.length > 0 && (
                                <>
                                  <div style={{ fontWeight: 700, margin: "6px 0 6px" }}>
                                    Related to this week
                                  </div>
                                  {related.map((g) => (
                                    <Row key={`rel-${g.id}`} g={g} />
                                  ))}
                                </>
                              )}

                              {others.length > 0 && (
                                <>
                                  <div style={{ fontWeight: 700, margin: "10px 0 6px" }}>
                                    Other goals
                                  </div>
                                  {others.map((g) => (
                                    <Row key={`oth-${g.id}`} g={g} />
                                  ))}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        )}

        {/* GOALS TIMELINE VIEW */}
        {selectedClientId && goalsTab === "goals" && (
          <section className={styles.details}>
            <div style={{ ...TL.goalsToolbar, marginBottom: 10 }}>
              <input
                placeholder="Search goals…"
                value={goalSearch}
                onChange={(e) => setGoalSearch(e.target.value)}
                style={TL.search}
              />
              <select
                value={goalStatusFilter}
                onChange={(e) => setGoalStatusFilter(e.target.value)}
                style={TL.select}
              >
                {["All", "Planned", "In Progress", "At Risk", "Done"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <div style={TL.spacer} />
              {canEdit && (
                <button style={TL.primaryBtn} onClick={openCreateGoal}>
                  + New Goal
                </button>
              )}
            </div>

            {loadingGoals ? (
              <div className={styles.dim}>Loading goals…</div>
            ) : goalsByYear.length === 0 ? (
              <div className={styles.dim}>No goals yet. Create one to start tracking achievements.</div>
            ) : (
              <div style={{ display: "grid", gap: 16 }}>
                {goalsByYear.map(([year, arr]) => (
                  <div key={year} style={TL.timelineWrap}>
                    <div style={TL.groupTitle}>{year}</div>
                    <div style={TL.group}>
                      {arr.map((g) => {
                        const type = g.type === "count" ? "count" : "percent";
                        const t = Number(g.targetCount || 0);
                        const c = Number(g.currentCount || 0);
                        const derived = type === "count"
                          ? deriveProgressForCount(c, t)
                          : clamp(Number(g.progress ?? 0), 0, 100);
                        const color = statusColor(g.status || "Planned");

                        const rangeChip = (
                          <span style={TL.chip("#f8fafc", "#1f2937", "#e5e7eb")}>
                            <span>📅</span>
                            <span>
                              {g.startDate || "?"} → {g.targetDate || "?"}
                            </span>
                          </span>
                        );
                        const statusChip = (
                          <span style={TL.chip("#fff", color, color)}>
                            ● <span>{g.status || "Planned"}</span>
                          </span>
                        );
                        const countChip =
                          type === "count" ? (
                            <span style={TL.chip("#ecfeff", "#0e7490", "#a5f3fc")}>
                              🔢 {c}/{t} {g.unitLabel || ""}
                            </span>
                          ) : null;
                        const weeksChip =
                          Array.isArray(g.relatedWeeks) && g.relatedWeeks.length ? (
                            <span style={TL.chip("#eff6ff", "#1d4ed8", "#bfdbfe")}>
                              🔗 Weeks:{" "}
                              {g.relatedWeeks.map((wk, i) => (
                                <button
                                  key={wk + i}
                                  type="button"
                                  onClick={() => {
                                    setGoalsTab("reports");
                                    setActiveWeekKey(wk);
                                  }}
                                  style={{
                                    color: "#1d4ed8",
                                    textDecoration: "underline",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                  }}
                                  title="Open related week"
                                >
                                  {wk}
                                </button>
                              )).reduce((prev, cur) => [prev, ", ", cur])}
                            </span>
                          ) : null;

                        return (
                          <div key={g.id} style={TL.tlRow}>
                            <div style={TL.tlRowInner}>
                              <div style={TL.title}>{g.title}</div>
                              {g.description && (
                                <div style={TL.desc}>{g.description}</div>
                              )}
                              <div style={TL.meta}>
                                {rangeChip}
                                {statusChip}
                                {countChip}
                                {weeksChip}
                              </div>
                              <div style={TL.barWrap} title={`${derived}%`}>
                                <div style={TL.bar(derived, color)} />
                              </div>
                            </div>
                            {canEdit && (
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  style={ICONBTN.base}
                                  onClick={() => openEditGoal(g)}
                                  aria-label="Edit goal"
                                  title="Edit"
                                >
                                  <Icon name="edit" />
                                </button>
                                <button
                                  style={{ ...ICONBTN.base, ...ICONBTN.danger }}
                                  onClick={() => deleteGoal(g.id)}
                                  disabled={deletingGoalId === g.id}
                                  aria-label="Delete goal"
                                  title="Delete"
                                >
                                  {deletingGoalId === g.id ? "…" : <Icon name="trash" />}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* ------- Weekly Work Modal ------- */}
      {showForm && (
        <div className={styles.modalWrap} onClick={() => !saving && setShowForm(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.h3}>
                {formMode === "create" ? "Add Client Work" : "Edit Client Work"}
              </h3>
              <button
                className={styles.close}
                onClick={() => setShowForm(false)}
                disabled={saving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSave} className={styles.form}>
              <div className={styles.row}>
                <label className={styles.label}>Client</label>
                {formMode === "edit" || formClientId ? (
                  <div className={styles.static}>
                    {formClientName}
                  </div>
                ) : (
                  <select
                    className={styles.select}
                    value={formClientId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormClientId(val);
                      const hit = allClients.find((c) => c.id === val);
                      setFormClientName(hit?.name || "");
                    }}
                    required
                    disabled={loadingAllClients}
                  >
                    <option value="">Select client…</option>
                    {allClients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className={styles.row}>
                <label className={styles.label}>Week (Monday)</label>
                <input
                  type="date"
                  value={formWeekKey}
                  onChange={(e) =>
                    setFormWeekKey(toYMD(startOfWeekMonday(new Date(e.target.value))))
                  }
                  className={styles.input}
                  title="Pick any day; we'll use that week's Monday"
                  required
                />
                <div className={styles.mutedSmall}>
                  {rangeLabel(startOfWeekMonday(new Date(formWeekKey)))} ({formWeekKey})
                </div>
              </div>

              <div className={styles.row}>
                <label className={styles.label}>Highlights</label>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={form.highlights}
                  onChange={(e) => setForm((f) => ({ ...f, highlights: e.target.value }))}
                  placeholder="Key wins…"
                />
              </div>

              <div className={styles.groupHeader}>
                <div className={styles.groupTitle}>Work Items</div>
                <button type="button" className={styles.smallBtn} onClick={addWorkItem}>
                  + Add
                </button>
              </div>
              <div className={styles.itemsGrid}>
                {form.workItems.map((it, i) => (
                  <div key={`wi-${i}`} className={styles.itemRow}>
                    <input
                      className={styles.input}
                      placeholder="What was delivered"
                      value={it.text}
                      onChange={(e) => updateWorkItem(i, "text", e.target.value)}
                    />
                    <input
                      className={styles.input}
                      placeholder="Owner"
                      value={it.owner}
                      onChange={(e) => updateWorkItem(i, "owner", e.target.value)}
                    />
                    <select
                      className={styles.select}
                      value={it.status}
                      onChange={(e) => updateWorkItem(i, "status", e.target.value)}
                    >
                      <option>Done</option>
                      <option>In Progress</option>
                      <option>Blocked</option>
                    </select>
                    <button type="button" className={styles.remove} onClick={() => removeWorkItem(i)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className={styles.groupHeader}>
                <div className={styles.groupTitle}>Links</div>
                <button type="button" className={styles.smallBtn} onClick={addLink}>
                  + Add
                </button>
              </div>
              {form.links.map((l, i) => (
                <div key={`l-${i}`} className={styles.linkRow}>
                  <input
                    className={styles.input}
                    placeholder="https://…"
                    value={l}
                    onChange={(e) => updateLink(i, e.target.value)}
                  />
                  <button type="button" className={styles.remove} onClick={() => removeLink(i)}>
                    ×
                  </button>
                </div>
              ))}

              <div className={styles.groupHeader}>
                <div className={styles.groupTitle}>Metrics</div>
                <button type="button" className={styles.smallBtn} onClick={addMetric}>
                  + Add
                </button>
              </div>
              {form.metrics.map((m, i) => (
                <div key={`m-${i}`} className={styles.itemRow}>
                  <input
                    className={styles.input}
                    placeholder="Metric name"
                    value={m.key}
                    onChange={(e) => updateMetric(i, "key", e.target.value)}
                  />
                  <input
                    className={styles.input}
                    placeholder="Value"
                    value={m.value}
                    onChange={(e) => updateMetric(i, "value", e.target.value)}
                  />
                  <div className={styles.spacer} />
                  <button type="button" className={styles.remove} onClick={() => removeMetric(i)}>
                    ×
                  </button>
                </div>
              ))}

              <div className={styles.row} style={{ marginTop: 6 }}>
                <label className={styles.label}>Blockers / Risks</label>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={form.blockers}
                  onChange={(e) => setForm((f) => ({ ...f, blockers: e.target.value }))}
                />
              </div>

              <div className={styles.row}>
                <label className={styles.label}>Next Week</label>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={form.nextWeek}
                  onChange={(e) => setForm((f) => ({ ...f, nextWeek: e.target.value }))}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.outline}
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primary} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------- Goal Modal ------- */}
      {showGoalModal && (
        <div className={styles.modalWrap} onClick={() => !goalSaving && setShowGoalModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.h3}>
                {goalMode === "create" ? "Add Goal" : "Edit Goal"}
              </h3>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {goalMode === "edit" && (
                  <button
                    style={{ ...ICONBTN.base, ...ICONBTN.danger }}
                    onClick={() => deleteGoal(goalEditingId)}
                    disabled={deletingGoalId === goalEditingId || goalSaving}
                    aria-label="Delete goal"
                    title="Delete"
                  >
                    {deletingGoalId === goalEditingId ? "…" : <Icon name="trash" />}
                  </button>
                )}
                <button
                  className={styles.close}
                  onClick={() => setShowGoalModal(false)}
                  disabled={goalSaving}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={saveGoal} className={styles.form}>
              <div className={styles.row}>
                <label className={styles.label}>Title</label>
                <input
                  className={styles.input}
                  value={goalForm.title}
                  onChange={(e) => setGoalForm((f) => ({ ...f, title: e.target.value }))}
                  required
                  placeholder="e.g., Launch v2 of client site"
                />
              </div>

              <div className={styles.row}>
                <label className={styles.label}>Description</label>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={goalForm.description}
                  onChange={(e) => setGoalForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="What does success look like?"
                />
              </div>

              {/* Type selector + type-specific fields */}
              <div className={styles.itemsGrid}>
                <div className={styles.itemRow}>
                  <div style={{ flex: 1 }}>
                    <label className={styles.label}>Goal Type</label>
                    <select
                      className={styles.select}
                      value={goalForm.type}
                      onChange={(e) => {
                        const nextType = e.target.value === "count" ? "count" : "percent";
                        setGoalForm((f) => ({
                          ...f,
                          type: nextType,
                          targetCount: nextType === "count" ? (f.targetCount || 1) : f.targetCount,
                          currentCount: nextType === "count" ? (f.currentCount || 0) : f.currentCount,
                          unitLabel: nextType === "count" ? (f.unitLabel || "") : f.unitLabel,
                        }));
                      }}
                    >
                      <option value="percent">Percent (manual %)</option>
                      <option value="count">Count (auto % from counts)</option>
                    </select>
                  </div>
                </div>

                {goalForm.type === "count" ? (
                  <div className={styles.itemRow}>
                    <div style={{ flex: 1 }}>
                      <label className={styles.label}>Unit Label</label>
                      <input
                        className={styles.input}
                        placeholder="e.g., videos, posts"
                        value={goalForm.unitLabel}
                        onChange={(e) => setGoalForm((f) => ({ ...f, unitLabel: e.target.value }))}
                        required
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className={styles.label}>Target Count</label>
                      <input
                        type="number"
                        className={styles.input}
                        min={1}
                        value={goalForm.targetCount}
                        onChange={(e) =>
                          setGoalForm((f) => {
                            const targetCount = Math.max(1, +e.target.value || 1);
                            const progress = deriveProgressForCount(f.currentCount, targetCount);
                            return { ...f, targetCount, progress };
                          })
                        }
                        required
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className={styles.label}>Current Count</label>
                      <input
                        type="number"
                        className={styles.input}
                        min={0}
                        value={goalForm.currentCount}
                        onChange={(e) =>
                          setGoalForm((f) => {
                            const currentCount = Math.max(0, +e.target.value || 0);
                            const progress = deriveProgressForCount(currentCount, f.targetCount);
                            return { ...f, currentCount, progress };
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div className={styles.itemRow}>
                    <div style={{ flex: 1 }}>
                      <label className={styles.label}>Progress (%)</label>
                      <input
                        type="number"
                        className={styles.input}
                        value={goalForm.progress}
                        min={0}
                        max={100}
                        onChange={(e) =>
                          setGoalForm((f) => ({ ...f, progress: clamp(Number(e.target.value || 0), 0, 100) }))
                        }
                        placeholder="0–100"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.itemsGrid}>
                <div className={styles.itemRow}>
                  <div style={{ flex: 1 }}>
                    <label className={styles.label}>Start Date</label>
                    <input
                      type="date"
                      className={styles.input}
                      value={goalForm.startDate}
                      onChange={(e) => setGoalForm((f) => ({ ...f, startDate: e.target.value }))}
                      required
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className={styles.label}>Target Date</label>
                    <input
                      type="date"
                      className={styles.input}
                      value={goalForm.targetDate}
                      onChange={(e) => setGoalForm((f) => ({ ...f, targetDate: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className={styles.itemRow}>
                  <div style={{ flex: 1 }}>
                    <label className={styles.label}>Status</label>
                    <select
                      className={styles.select}
                      value={goalForm.status}
                      onChange={(e) => setGoalForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      <option>Planned</option>
                      <option>In Progress</option>
                      <option>At Risk</option>
                      <option>Done</option>
                    </select>
                  </div>

                  {goalForm.type === "count" && (
                    <div style={{ flex: 1 }}>
                      <label className={styles.label}>Auto Progress</label>
                      <div className={styles.static}>
                        {deriveProgressForCount(goalForm.currentCount, goalForm.targetCount)}%
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.groupHeader}>
                <div className={styles.groupTitle}>Related Weekly Reports</div>
                <button
                  type="button"
                  className={styles.smallBtn}
                  onClick={() =>
                    setGoalForm((f) => ({
                      ...f,
                      relatedWeeks: [...(Array.isArray(f.relatedWeeks) ? f.relatedWeeks : []), toYMD(startOfWeekMonday(new Date()))],
                    }))
                  }
                >
                  + Add Current Week
                </button>
              </div>

              {(Array.isArray(goalForm.relatedWeeks) ? goalForm.relatedWeeks : []).map((wk, i) => (
                <div key={wk + i} className={styles.linkRow}>
                  <input
                    className={styles.input}
                    value={wk}
                    onChange={(e) =>
                      setGoalForm((f) => {
                        const arr = [...(Array.isArray(f.relatedWeeks) ? f.relatedWeeks : [])];
                        arr[i] = toYMD(startOfWeekMonday(new Date(e.target.value)));
                        return { ...f, relatedWeeks: arr };
                      })
                    }
                    type="date"
                    title="Pick any date; saved as that week's Monday"
                  />
                  <button type="button" className={styles.remove} onClick={() => {
                    setGoalForm((f) => {
                      const arr = Array.isArray(f.relatedWeeks) ? [...f.relatedWeeks] : [];
                      arr.splice(i, 1);
                      return { ...f, relatedWeeks: arr };
                    });
                  }}>
                    ×
                  </button>
                </div>
              ))}

              <div className={styles.modalActions}>
                {goalMode === "edit" && (
                  <button
                    type="button"
                    style={{ ...ICONBTN.base, ...ICONBTN.danger }}
                    onClick={() => deleteGoal(goalEditingId)}
                    disabled={deletingGoalId === goalEditingId || goalSaving}
                    aria-label="Delete goal"
                    title="Delete"
                  >
                    {deletingGoalId === goalEditingId ? "…" : <Icon name="trash" />}
                  </button>
                )}
                <div className={styles.spacer} />
                <button
                  type="button"
                  className={styles.outline}
                  onClick={() => setShowGoalModal(false)}
                  disabled={goalSaving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primary} disabled={goalSaving}>
                  {goalSaving ? "Saving…" : "Save Goal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
